'use client';

import { type ComponentType, type ReactElement, type ReactNode } from 'react';

import { create } from '@/libs';

export type ModalOptions = {
  content?: ReactNode;
  Component?: ComponentType | ReactElement;
  className?: string;

  hardClose?: boolean;
  showCloseButton?: boolean;
  priority?: number;
  onCloseOptional?: () => void;
  showPreventClose?: boolean;
  onPreventClose?: () => ReactNode;
};

export type ModalData = ModalOptions & {
  id: string;
  openedAt: number;
};

type ModalStore = {
  modals: ModalData[];
  openModal: (options: Omit<ModalOptions, 'openedAt' | 'id'>) => string;
  closeModal: (id: string) => void;
  closeLastModal: () => void;
  showPreventClose?: boolean;
  preventingComponent?: ReactNode;
  updateShouldCallPreventClose: (status: boolean) => void;
  clearPreventingComponent: () => void;
  forceCloseModalWithPreventClear: () => void;
};

const compareModal = (modalX: ModalData, modalY: ModalData) => {
  const diffPriority = (modalY.priority ?? 0) - (modalX.priority ?? 0);
  if (diffPriority !== 0) return diffPriority;
  return modalY.openedAt - modalX.openedAt;
};

export const useModalStore = create<ModalStore>((set, get) => ({
  modals: [],

  preventingComponent: undefined,

  openModal: (options) => {
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);

    const modal: ModalData = {
      id,
      openedAt: Date.now(),
      hardClose: false,
      showCloseButton: true,
      priority: 0,
      ...options,
    };

    set((state) => ({
      modals: [...state.modals, modal].sort(compareModal),
    }));

    return id;
  },

  closeModal: (id) => {
    const showPreventClose = get().showPreventClose;
    const modal = get().modals.find((m) => m.id === id);
    if (!modal) return;

    if (modal.onPreventClose && (showPreventClose || modal.showPreventClose)) {
      const node = modal.onPreventClose();
      set({ preventingComponent: node });
      return;
    }

    modal.onCloseOptional?.();
    set((state) => ({
      modals: state.modals.filter((m) => m.id !== id),
    }));
  },

  closeLastModal: () => {
    const showPreventClose = get().showPreventClose;
    const modal = get().modals[0];
    if (!modal) return;

    if (modal.onPreventClose && (showPreventClose || modal.showPreventClose)) {
      const node = modal.onPreventClose();
      set({ preventingComponent: node });
      return;
    }

    modal.onCloseOptional?.();
    set((state) => {
      const [, ...rest] = state.modals;
      return { modals: rest };
    });
  },

  updateShouldCallPreventClose: (status: boolean) => {
    set({ showPreventClose: status });
  },

  clearPreventingComponent: () => {
    set({ preventingComponent: undefined });
  },

  forceCloseModalWithPreventClear: () => {
    const modal = get().modals[0];
    if (!modal) return;

    modal.onCloseOptional?.();
    set((state) => {
      const [, ...rest] = state.modals;
      return {
        modals: rest,
        preventingComponent: undefined,
      };
    });
  },
}));
